import React, { useState, useEffect } from "react";
import { Palette, Upload, Info, X, RotateCcw } from "lucide-react";
import { useOutletContext } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import ViewWebsiteButton from "../../components/ui/ViewWebsiteButton";
import SectionHeader from "../../components/ui/SectionHeader";
import ToggleSetting from "../../components/ui/ToggleSetting";
import FormField from "../../components/ui/FormField";
import {
  getWebsiteSettings,
  getThemeSettings,
  updateThemeSettings,
  resetThemeSettings,
  ThemeColorDefaults,
} from "../../services/websitesetting";
import { validateImage } from "../../lib/image";
import GlobalLoader from "../../components/ui/GlobalLoader";
import { useToast } from "../../hooks/useToast";
import InfoContainer from "../../components/ui/InfoContainer";
import WebsiteSectionCard from "../../components/ui/WebsiteSectionCard";
import PageHeader from "../../components/ui/PageHeader";

const FALLBACK_DEFAULTS: ThemeColorDefaults = {
  primary_color: "#00c1cf",
  secondary_color: "#373737",
  heading_color: "#ffffff",
  text_color: "#e9e9e9",
};

const COLOR_FIELDS: {
  key: keyof ThemeColorDefaults;
  label: string;
  hint: string;
}[] = [
  {
    key: "primary_color",
    label: "Primary",
    hint: "Buttons, accents, Book Now",
  },
  {
    key: "secondary_color",
    label: "Secondary",
    hint: "Dark surfaces, footer, heroes",
  },
  {
    key: "heading_color",
    label: "Heading",
    hint: "Hero & section titles",
  },
  {
    key: "text_color",
    label: "Text",
    hint: "Body copy & supporting text",
  },
];

/** Always returns lowercase #rrggbb for <input type="color"> (browsers reject uppercase). */
function normalizeHex(value: string | null | undefined, fallback = "#000000"): string {
  if (!value) return fallback.toLowerCase();
  let raw = String(value).trim();

  // rgb(r,g,b) / rgba(...)
  const rgb = raw.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i
  );
  if (rgb) {
    const toHex = (n: string) =>
      Math.max(0, Math.min(255, Number(n)))
        .toString(16)
        .padStart(2, "0");
    return `#${toHex(rgb[1])}${toHex(rgb[2])}${toHex(rgb[3])}`.toLowerCase();
  }

  if (!raw.startsWith("#")) raw = `#${raw}`;

  // #rgb → #rrggbb
  if (/^#[0-9A-Fa-f]{3}$/.test(raw)) {
    const r = raw[1];
    const g = raw[2];
    const b = raw[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }

  if (/^#[0-9A-Fa-f]{6}$/.test(raw)) return raw.toLowerCase();

  return fallback.toLowerCase();
}

const FrontThemeSettings = () => {
  const { subdomain } = useOutletContext<{ subdomain: string }>();
  const [logoVisibility, setLogoVisibility] = useState(true);
  const [websiteTitle, setWebsiteTitle] = useState("");
  const [bookingUrl, setBookingUrl] = useState("");
  const [bookingButtonLabel, setBookingButtonLabel] = useState("");
  const [themeId, setThemeId] = useState<number | null>(null);
  const [logoFile, setLogoFile] = useState<string | null>(null);
  const [logoUpload, setLogoUpload] = useState<File | null>(null);
  const [primaryColor, setPrimaryColor] = useState(FALLBACK_DEFAULTS.primary_color);
  const [secondaryColor, setSecondaryColor] = useState(FALLBACK_DEFAULTS.secondary_color);
  const [headingColor, setHeadingColor] = useState(FALLBACK_DEFAULTS.heading_color);
  const [textColor, setTextColor] = useState(FALLBACK_DEFAULTS.text_color);
  const [themeDefaults, setThemeDefaults] = useState<ThemeColorDefaults>(FALLBACK_DEFAULTS);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const toast = useToast();

  const colors: ThemeColorDefaults = {
    primary_color: primaryColor,
    secondary_color: secondaryColor,
    heading_color: headingColor,
    text_color: textColor,
  };

  const setColor = (key: keyof ThemeColorDefaults, value: string) => {
    switch (key) {
      case "primary_color":
        setPrimaryColor(value);
        break;
      case "secondary_color":
        setSecondaryColor(value);
        break;
      case "heading_color":
        setHeadingColor(value);
        break;
      case "text_color":
        setTextColor(value);
        break;
    }
  };

  const isDefaultPalette =
    primaryColor === normalizeHex(themeDefaults.primary_color) &&
    secondaryColor === normalizeHex(themeDefaults.secondary_color) &&
    headingColor === normalizeHex(themeDefaults.heading_color) &&
    textColor === normalizeHex(themeDefaults.text_color);

  const applyThemeColors = (
    theme: {
      primary_color?: string | null;
      secondary_color?: string | null;
      heading_color?: string | null;
      text_color?: string | null;
    },
    defaults: ThemeColorDefaults
  ) => {
    // Prefer actual DB values; only fall back when field is missing/invalid
    const primary = normalizeHex(theme?.primary_color, defaults.primary_color);
    const secondary = normalizeHex(theme?.secondary_color, defaults.secondary_color);
    const heading = normalizeHex(theme?.heading_color, defaults.heading_color);
    const text = normalizeHex(theme?.text_color, defaults.text_color);
    setPrimaryColor(primary);
    setSecondaryColor(secondary);
    setHeadingColor(heading);
    setTextColor(text);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // 1) Dedicated theme API — explicit colors for active template
        // 2) Fallback: website-settings resolve() row
        let theme: {
          id: number;
          title?: string;
          booking_url?: string | null;
          booking_button_label?: string | null;
          is_logo_visible?: number;
          logo?: string | null;
          primary_color?: string | null;
          secondary_color?: string | null;
          heading_color?: string | null;
          text_color?: string | null;
        } | null = null;
        let defaultsRaw: ThemeColorDefaults | null = null;

        try {
          const themePayload = await getThemeSettings();
          theme = themePayload.theme;
          defaultsRaw = themePayload.defaults;
        } catch (e) {
          console.warn("getThemeSettings failed, falling back to website-settings", e);
        }

        const settings = await getWebsiteSettings();
        if (!theme && settings.website_theme_settings) {
          theme = settings.website_theme_settings;
        }
        if (!defaultsRaw && settings.theme_defaults) {
          defaultsRaw = settings.theme_defaults;
        }
        // If theme-settings returned a theme without colors, merge from website-settings
        if (theme && settings.website_theme_settings) {
          const ws = settings.website_theme_settings;
          theme = {
            ...ws,
            ...theme,
            primary_color: theme.primary_color || ws.primary_color,
            secondary_color: theme.secondary_color || ws.secondary_color,
            heading_color: theme.heading_color || ws.heading_color,
            text_color: theme.text_color || ws.text_color,
          };
        }

        const defaults: ThemeColorDefaults = {
          primary_color: normalizeHex(
            defaultsRaw?.primary_color,
            FALLBACK_DEFAULTS.primary_color
          ),
          secondary_color: normalizeHex(
            defaultsRaw?.secondary_color,
            FALLBACK_DEFAULTS.secondary_color
          ),
          heading_color: normalizeHex(
            defaultsRaw?.heading_color,
            FALLBACK_DEFAULTS.heading_color
          ),
          text_color: normalizeHex(
            defaultsRaw?.text_color,
            FALLBACK_DEFAULTS.text_color
          ),
        };
        setThemeDefaults(defaults);

        if (theme) {
          setThemeId(theme.id);
          setWebsiteTitle(theme.title || "");
          setBookingUrl(theme.booking_url || "");
          setBookingButtonLabel(theme.booking_button_label || "");
          setLogoVisibility(Number(theme.is_logo_visible) === 1);
          applyThemeColors(theme, defaults);

          if (theme.logo) {
            setLogoFile(
              theme.logo.startsWith("http")
                ? theme.logo
                : `https://salonify.co.uk/user-uploads/front-logo/${theme.logo}`
            );
          }
        }
      } catch (err: any) {
        console.error("fetchData ERROR:", err);
        toast.error("Failed to load theme settings. Please refresh.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        await validateImage(file);
        if (file.size > 2 * 1024 * 1024) {
          throw new Error("Logo size should be less than 2MB");
        }
        setLogoUpload(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          setLogoFile(e.target?.result as string);
        };
        reader.readAsDataURL(file);
        setErrors((prev) => ({ ...prev, logo: "" }));
      } catch (err: any) {
        toast.error(err.message || "Invalid image");
        setErrors((prev) => ({
          ...prev,
          logo: err.message || "Invalid image",
        }));
      }
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!websiteTitle.trim()) {
      newErrors.title = "Website title is required";
    } else if (websiteTitle.length > 50) {
      newErrors.title = "Title cannot exceed 50 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error("Please fix the validation errors.");
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      if (!themeId) {
        toast.error("Theme configuration not loaded properly.");
        return;
      }
      await updateThemeSettings(themeId, {
        title: websiteTitle,
        is_logo_visible: logoVisibility,
        booking_url: bookingUrl.trim(),
        booking_button_label: bookingButtonLabel.trim(),
        primary_color: normalizeHex(primaryColor),
        secondary_color: normalizeHex(secondaryColor),
        heading_color: normalizeHex(headingColor),
        text_color: normalizeHex(textColor),
        logo: logoUpload,
        favicon: null,
      });
      toast.success("Front theme updated successfully!");
    } catch (error: any) {
      console.error("handleSave ERROR:", error);
      if (error.type === "serverValidation") {
        setErrors(error.errors);
        toast.error("Validation failed. Please check the fields.");
      } else {
        const status = error.response?.status;
        let userMessage = "Something went wrong while saving settings.";

        if (status === 404) {
          userMessage =
            "The theme settings could not be found. Please refresh and try again.";
        } else if (status === 403) {
          userMessage = "You do not have permission to update these settings.";
        } else if (status === 413) {
          userMessage =
            "The uploaded file is too large for the server to process.";
        } else if (status === 500) {
          userMessage = "Server error occurred. Our team has been notified.";
        } else if (error.message === "Network Error") {
          userMessage =
            "Connection lost. Please check your internet and try again.";
        }
        toast.error(userMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetColors = async () => {
    if (
      !window.confirm(
        "Reset colors to the original theme palette? Your logo and title will not be changed."
      )
    ) {
      return;
    }

    setIsResetting(true);
    try {
      const result = await resetThemeSettings();
      const defaults: ThemeColorDefaults = {
        primary_color: normalizeHex(
          result.defaults?.primary_color,
          themeDefaults.primary_color
        ),
        secondary_color: normalizeHex(
          result.defaults?.secondary_color,
          themeDefaults.secondary_color
        ),
        heading_color: normalizeHex(
          result.defaults?.heading_color,
          themeDefaults.heading_color
        ),
        text_color: normalizeHex(
          result.defaults?.text_color,
          themeDefaults.text_color
        ),
      };
      setThemeDefaults(defaults);
      if (result.theme?.id) setThemeId(result.theme.id);
      applyThemeColors(result.theme || defaults, defaults);
      toast.success("Theme colors reset to original defaults.");
    } catch (err) {
      console.error("handleResetColors ERROR:", err);
      toast.error("Failed to reset theme colors.");
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <>
      {(isLoading || isSubmitting || isResetting) && (
        <GlobalLoader
          initialPageLoad={true}
          fullScreen={false}
          description={
            isResetting
              ? "Resetting theme colors..."
              : isSubmitting
              ? "Updating branding settings..."
              : "Loading theme settings..."
          }
        />
      )}
      <div className="space-y-5">
        <PageHeader
          title="Front Theme Settings"
          description="Customize your website's branding and appearance"
          variant="card"
          className="mb-5"
          mobileActionsInHeader={true}
          actions={<ViewWebsiteButton subdomain={subdomain} />}
        />

        <WebsiteSectionCard title="Branding & Theme" icon={Palette}>
          <div className="space-y-8">
            {/* Website title */}
            <div>
              <SectionHeader title="Website Information" />
              <InfoContainer
                message="Set your website title and branding elements"
                iconSize={13}
                className="mb-4"
              />
              <FormField
                label="Website Title *"
                name="websiteTitle"
                value={websiteTitle}
                onChange={(name, value) => {
                  setWebsiteTitle(value);
                  if (errors.title)
                    setErrors((prev) => ({ ...prev, title: "" }));
                }}
                type="text"
                placeholder="Enter website title"
                className="max-w-xl"
                error={errors.title || errors.website_title}
              />
              <p className="text-xs text-gray-500 mt-1.5">
                Shown in browser tabs and search results
              </p>
              <div className="mt-5 max-w-xl">
                <FormField
                  label="Booking URL"
                  name="bookingUrl"
                  value={bookingUrl}
                  onChange={(_, value) => {
                    setBookingUrl(value);
                    if (errors.booking_url)
                      setErrors((prev) => ({ ...prev, booking_url: "" }));
                  }}
                  type="url"
                  placeholder="https://…"
                  error={errors.booking_url}
                />
                <p className="text-xs text-gray-500 mt-1.5">
                  Used on all Book Now buttons. Leave empty to use the default Glossa booking page.
                </p>
              </div>
              <div className="mt-5 max-w-xl">
                <FormField
                  label="Booking button label"
                  name="bookingButtonLabel"
                  value={bookingButtonLabel}
                  onChange={(_, value) => {
                    setBookingButtonLabel(value);
                    if (errors.booking_button_label)
                      setErrors((prev) => ({
                        ...prev,
                        booking_button_label: "",
                      }));
                  }}
                  type="text"
                  placeholder="e.g. Book Now, Signup, Book Appointment"
                  error={errors.booking_button_label}
                />
                <p className="text-xs text-gray-500 mt-1.5">
                  Label shown on header, hero, and service Book Now buttons across your website. Leave empty for “Book Now”.
                </p>
              </div>
            </div>

            {/* Theme colors */}
            <div className="border-t border-gray-100 pt-8">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <SectionHeader title="Theme Colors" />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleResetColors}
                  disabled={isResetting || isSubmitting || isDefaultPalette}
                  className="shrink-0 border-gray-200 text-gray-600 hover:bg-gray-50"
                >
                  <RotateCcw size={14} />
                  Reset to original
                </Button>
              </div>

              {/* Live preview strip */}
              <div
                className="mt-5 mb-5 rounded-lg overflow-hidden border border-gray-200"
                style={{ background: secondaryColor }}
              >
                <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0">
                    <p
                      className="text-base font-semibold truncate"
                      style={{ color: headingColor }}
                    >
                      Preview heading
                    </p>
                    <p
                      className="text-sm mt-0.5 truncate"
                      style={{ color: textColor }}
                    >
                      Supporting text on dark surfaces
                    </p>
                  </div>
                  <span
                    className="inline-flex self-start items-center rounded-full px-4 py-1.5 text-xs font-medium text-white shrink-0"
                    style={{ background: primaryColor }}
                  >
                    {bookingButtonLabel.trim() || "Book Now"}
                  </span>
                </div>
                <div className="flex h-2">
                  {COLOR_FIELDS.map((field) => (
                    <div
                      key={field.key}
                      className="flex-1"
                      style={{ background: colors[field.key] }}
                      title={field.label}
                    />
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
                {COLOR_FIELDS.map((field) => {
                  const value = colors[field.key];
                  return (
                    <div
                      key={field.key}
                      className="rounded-lg border border-gray-200 bg-white p-3.5 hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <label className="relative shrink-0 cursor-pointer group">
                          <input
                            type="color"
                            value={normalizeHex(value, themeDefaults[field.key])}
                            onChange={(e) =>
                              setColor(field.key, e.target.value.toLowerCase())
                            }
                            className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                            aria-label={field.label}
                          />
                          <span
                            className="block h-11 w-11 rounded-md border border-gray-200 shadow-sm ring-offset-2 group-hover:ring-2 group-hover:ring-[#885ABB]/30 transition"
                            style={{
                              background: normalizeHex(
                                value,
                                themeDefaults[field.key]
                              ),
                            }}
                          />
                        </label>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-800">
                            {field.label}
                          </p>
                          <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                            {field.hint}
                          </p>
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => setColor(field.key, e.target.value)}
                            onBlur={(e) =>
                              setColor(
                                field.key,
                                normalizeHex(
                                  e.target.value,
                                  themeDefaults[field.key]
                                )
                              )
                            }
                            className="mt-2 w-full h-8 px-2.5 text-xs font-mono lowercase tracking-wide border border-gray-200 rounded-md bg-gray-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#885ABB] focus:border-[#885ABB]"
                            spellCheck={false}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Logo */}
            <div className="border-t border-gray-100 pt-8">
              <SectionHeader title="Logo" />
              <div className="mt-4 max-w-sm">
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                  />
                  <label
                    htmlFor="logo-upload"
                    className={`border-2 border-dashed rounded-lg h-44 flex flex-col items-center justify-center cursor-pointer transition-all group ${
                      errors.logo
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300 hover:border-[#885ABB] bg-gray-50"
                    }`}
                  >
                    {logoFile ? (
                      <div className="relative w-full h-full flex items-center justify-center p-4">
                        <img
                          src={logoFile}
                          alt="Logo preview"
                          className="max-h-32 max-w-full object-contain"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setLogoFile(null);
                            setLogoUpload(null);
                          }}
                          className="absolute top-2 right-2 w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md transition-all z-10"
                          title="Remove image"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Upload
                          className={`h-10 w-10 mb-2 transition-colors ${
                            errors.logo
                              ? "text-red-400"
                              : "text-gray-400 group-hover:text-[#885ABB]"
                          }`}
                        />
                        <p className="text-gray-600 text-sm font-medium">
                          Click to upload logo
                        </p>
                        <p className="text-gray-400 text-xs mt-1">
                          PNG or JPG
                        </p>
                      </>
                    )}
                  </label>
                </div>
                {errors.logo ? (
                  <p className="text-xs text-red-500 mt-2">{errors.logo}</p>
                ) : (
                  <p className="text-xs mt-2 flex items-center gap-1 text-gray-500">
                    <Info size={11} />
                    Recommended max size: 150 × 150 pixels
                  </p>
                )}
              </div>
            </div>

            {/* Display */}
            <div className="border-t border-gray-100 pt-8">
              <SectionHeader title="Display Settings" />
              <div className="mt-3">
                <ToggleSetting
                  label="Logo Visibility"
                  description="Show or hide logo on your website"
                  checked={logoVisibility}
                  onChange={setLogoVisibility}
                />
              </div>
            </div>

            <div className="pt-2 flex justify-end border-t border-gray-100">
              <Button
                onClick={handleSave}
                disabled={isSubmitting || isResetting}
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-6 py-2.5 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200 mt-6"
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </WebsiteSectionCard>
      </div>
    </>
  );
};

export default FrontThemeSettings;
